<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="modelValue" class="slm-backdrop" @click.self="$emit('update:modelValue', false)">
        <div class="slm-sheet">
          <!-- Back button (top-left, absolute) -->
          <button v-if="step === 'otp'" class="slm-back slm-back--left" @click="step = 'email'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <!-- Close button (top-right, absolute) -->
          <button class="slm-back slm-back--right" @click="$emit('update:modelValue', false)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>

          <!-- Header -->
          <div class="slm-header">
            <h2 class="slm-title">{{ step === 'otp' ? t(mode === 'register' ? 'auth.modal.titleRegister' : 'auth.modal.titleLogin') : t(mode === 'register' ? 'auth.modal.titleRegister' : 'auth.modal.titleLogin') }}</h2>
            <p v-if="step === 'otp'" class="slm-sub">{{ t('auth.modal.otpSubtitle', { email: form.email }) }}</p>
          </div>

          <!-- Mode switch link — visible on email step only -->
          <div v-if="step === 'email'" class="slm-mode-switch">
            <template v-if="mode === 'login'">
              {{ t('auth.modal.noAccount') }}
              <button type="button" class="slm-mode-link" @click="switchMode('register')">{{ t('auth.modal.signUp') }}</button>
            </template>
            <template v-else>
              {{ t('auth.modal.hasAccount') }}
              <button type="button" class="slm-mode-link" @click="switchMode('login')">{{ t('auth.modal.signIn') }}</button>
            </template>
          </div>

          <!-- Google OAuth button -->
          <div v-if="step === 'email' && googleAuthUrl" class="slm-oauth">
            <a :href="googleAuthUrl" class="slm-oauth-btn slm-oauth-btn--google">
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              {{ t('auth.modal.continueWithGoogle') }}
            </a>
          </div>
          <div v-if="step === 'email' && googleAuthUrl" class="slm-divider">
            <span>{{ t('auth.modal.or') }}</span>
          </div>

          <!-- Step 1: Email form (login=email only, register=email+name) -->
          <form v-if="step === 'email'" class="slm-form" @submit.prevent="handleEmailStep">
            <div v-if="mode === 'register'" class="slm-field">
              <label class="slm-label">{{ t('auth.modal.fullname') }}</label>
              <input ref="nameInput" v-model="form.fullname" class="slm-input" type="text" :placeholder="t('auth.modal.fullnamePlaceholder')" required autocomplete="name" />
            </div>
            <div class="slm-field">
              <label class="slm-label">{{ t('auth.modal.email') }}</label>
              <input v-model="form.email" class="slm-input" type="email" :placeholder="t('auth.modal.emailPlaceholder')" required autocomplete="email" />
            </div>
            <p v-if="error" class="slm-error">{{ error }}</p>
            <button type="submit" class="slm-btn-primary" :disabled="loading">
              <span v-if="loading" class="slm-spinner" />
              <span v-else>{{ t('auth.modal.sendCode') }}</span>
            </button>
            <p class="slm-consent">
              {{ t('auth.modal.consentPrefix') }}
              <NuxtLink :to="`/${locale}/privacy-policy`" target="_blank" rel="noopener" class="slm-consent-link">{{ t('auth.modal.consentPrivacy') }}</NuxtLink>
              {{ t('auth.modal.consentAnd') }}
              <NuxtLink :to="`/${locale}/terms`" target="_blank" rel="noopener" class="slm-consent-link">{{ t('auth.modal.consentTerms') }}</NuxtLink>
            </p>
          </form>

          <!-- Step 2: OTP -->
          <form v-else-if="step === 'otp'" class="slm-form" @submit.prevent="handleOtpStep">
            <div class="slm-field">
              <label class="slm-label">{{ t('auth.modal.otpLabel') }}</label>
              <input ref="otpInput" v-model="form.otp" class="slm-input slm-input--otp" type="text" inputmode="numeric" pattern="[0-9]{6}" maxlength="6" :placeholder="'• • • • • •'" required autocomplete="one-time-code" />
            </div>
            <p v-if="error" class="slm-error">{{ error }}</p>
            <button type="submit" class="slm-btn-primary" :disabled="loading">
              <span v-if="loading" class="slm-spinner" />
              <span v-else>{{ t('auth.modal.confirm') }}</span>
            </button>
          </form>

          <!-- Step 3: Success -->
          <div v-else class="slm-success">
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="slm-success-icon"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10 10-4.477 10-10Z"/><path d="m8 12.5 2.5 2.5L16 9" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <p class="slm-success-text">{{ t('auth.modal.success') }}</p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useStudentStore } from '@/stores/student'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [v: boolean]; success: [] }>()

const { t, locale } = useI18n()
const store = useStudentStore()
const config = useRuntimeConfig()
const { url: googleAuthUrl } = storeToRefs(useGoogleAuthStore())

const step = ref<'email' | 'otp' | 'success'>('email')
const mode = ref<'login' | 'register'>('login')
const loading = ref(false)
const error = ref('')
const resendCooldown = ref(0)
const otpInput = ref<HTMLInputElement | null>(null)
const nameInput = ref<HTMLInputElement | null>(null)
const form = reactive({ email: '', fullname: '', otp: '' })

function switchMode (m: 'login' | 'register') {
  mode.value = m
  error.value = ''
  form.fullname = ''
}

const apiBase = computed(() => import.meta.dev ? "/_api" : ((config.public.NUXT_APP_LANDING_API as string) || "https://v2-api.bibleawr.com"))

async function handleEmailStep() {
  error.value = ''
  loading.value = true
  try {
    if (mode.value === 'register') {
      const res = await fetch(`${apiBase.value}/my/auth/otp/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email: form.email, fullname: form.fullname.trim(), locale: locale.value }),
      })
      if (!res.ok) { error.value = t('auth.modal.sendError'); return }
    } else {
      const res = await fetch(`${apiBase.value}/my/auth/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email: form.email, locale: locale.value }),
      })
      if (res.status === 404) { error.value = t('auth.modal.emailNotFound'); return }
      if (!res.ok) { error.value = t('auth.modal.sendError'); return }
    }
    step.value = 'otp'
    startResendCooldown()
    await nextTick()
    otpInput.value?.focus()
  } catch { error.value = t('auth.modal.sendError') }
  finally { loading.value = false }
}

async function handleOtpStep() {
  error.value = ''
  loading.value = true
  try {
    const res = await fetch(`${apiBase.value}/my/auth/otp/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ email: form.email, otp: form.otp }) })
    if (res.status === 401) { error.value = t('auth.modal.invalidCode'); return }
    if (!res.ok) { error.value = t('auth.modal.verifyError'); return }
    step.value = 'success'
    store.markLoggedIn()
    await store.fetchProfile()
    setTimeout(() => { emit('update:modelValue', false); emit('success'); navigateTo('/my/progress') }, 1400)
  } catch { error.value = t('auth.modal.verifyError') }
  finally { loading.value = false }
}

async function resend() { form.otp = ''; error.value = ''; await handleEmailStep() }

let timer: ReturnType<typeof setInterval> | null = null
function startResendCooldown() {
  resendCooldown.value = 60
  timer = setInterval(() => { if (resendCooldown.value > 0) resendCooldown.value--; else { clearInterval(timer!); timer = null } }, 1000)
}

watch(() => props.modelValue, (v) => {
  if (!v) { step.value = 'email'; mode.value = 'login'; error.value = ''; form.email = ''; form.otp = ''; form.fullname = ''; resendCooldown.value = 0 }
})
onUnmounted(() => { if (timer) clearInterval(timer) })
</script>

<style scoped>
.slm-backdrop {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.48); backdrop-filter: blur(6px);
  display: flex; align-items: flex-end; justify-content: center;
}
@media (min-width: 540px) {
  .slm-backdrop { align-items: center; }
}
.slm-sheet {
  font-family: var(--font-inter); position: relative;
  background: #fff; width: 100%; max-width: 420px;
  border-radius: 24px 24px 0 0; padding: 32px 24px 40px;
  max-height: 95vh; overflow-y: auto;
}
@media (min-width: 540px) {
  .slm-sheet { border-radius: 20px; padding: 32px 32px 40px; }
}

.slm-back {
  position: absolute; top: 8px;
  width: 40px; height: 40px; border-radius: 50%;
  background: none; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--color-grey); transition: background 0.15s; z-index: 1;
}
.slm-back--left  { left: 8px; }
.slm-back--right { right: 8px; }
.slm-back:hover { background: #F2F2F2; }

.slm-header { text-align: center; margin: 8px 0 20px; }
.slm-title { font-size: 28px; font-weight: var(--font-weight-bold); color: var(--color-dark-blue); margin: 0 0 6px; line-height: 1.25; }
.slm-sub { font-size: var(--font-size-small); color: var(--color-grey); margin: 0; line-height: 1.5; }

.slm-mode-switch {
  font-size: 17px; color: var(--color-grey);
  margin-bottom: 20px; display: flex; align-items: center; gap: 6px;
}
.slm-mode-link {
  background: none; border: none; padding: 0; cursor: pointer;
  font-family: var(--font-inter); font-size: 17px;
  font-weight: var(--font-weight-bold); color: var(--color-dark-blue);
  text-decoration: underline; text-decoration-color: transparent;
  transition: text-decoration-color 0.15s;
}
.slm-mode-link:hover { text-decoration-color: var(--color-dark-blue); }

.slm-oauth { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.slm-oauth-btn {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  min-height: 56px; background: var(--button-passive-bg);
  border: 1px solid #e5e7eb; border-radius: 100px;
  font-family: var(--font-inter); font-size: 15px; font-weight: var(--font-weight-semibold);
  color: var(--color-dark-blue); cursor: pointer;
  transition: background 0.15s; text-decoration: none;
}
.slm-oauth-btn:hover { background: #f3f4f6; }

.slm-divider { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.slm-divider::before, .slm-divider::after { content: ''; flex: 1; height: 1px; background: var(--color-grey-light); }
.slm-divider span { font-size: var(--font-size-small); color: var(--color-grey); font-weight: 500; }

.slm-form { display: flex; flex-direction: column; gap: 14px; }
.slm-field { display: flex; flex-direction: column; gap: 6px; }
.slm-label { font-size: var(--font-size-small); font-weight: var(--font-weight-semibold); color: var(--color-grey-dark); }
.slm-input {
  height: 52px; background: #f3f4f6;
  border: none; border-radius: 100px;
  padding: 0 20px; font-family: var(--font-inter); font-size: 16px;
  color: var(--color-dark-blue); outline: none;
  transition: box-shadow 0.15s; width: 100%; box-sizing: border-box;
}
.slm-input:focus { box-shadow: 0 0 0 3px rgba(9, 175, 234, 0.18); }
.slm-input--otp { text-align: center; font-size: 26px; letter-spacing: 10px; font-weight: var(--font-weight-bold); }

.slm-btn-primary {
  height: 56px;
  background: linear-gradient(90deg, var(--button-active-gradient-start) 0%, var(--button-active-gradient-end) 100%);
  color: var(--button-active-text); border: none; border-radius: 100px;
  font-family: var(--font-inter); font-size: 16px; font-weight: var(--font-weight-semibold);
  cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: opacity 0.2s, transform 0.15s; margin-top: 8px;
}
.slm-btn-primary:hover:not(:disabled) { opacity: 0.85; transform: scale(0.98); }
.slm-btn-primary:disabled { opacity: 0.6; cursor: default; transform: none; box-shadow: none; }

.slm-spinner {
  width: 20px; height: 20px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.35); border-top-color: #fff;
  animation: slm-spin 0.7s linear infinite;
}
@keyframes slm-spin { to { transform: rotate(360deg); } }

.slm-consent { font-size: 13px; color: var(--color-grey); text-align: center; margin: 4px 0 0; line-height: 1.5; }
.slm-consent-link { color: var(--color-grey-dark); text-decoration: underline; }
.slm-consent-link:hover { color: var(--color-dark-blue); }

.slm-error { font-size: var(--font-size-small); color: var(--color-error); margin: 0; }
.slm-footer-text { text-align: center; font-size: var(--font-size-small); color: var(--color-grey); margin: 4px 0 0; }
.slm-text-link {
  background: none; border: none; cursor: pointer;
  font-family: var(--font-inter); font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold); color: var(--color-dark-blue); padding: 0;
  text-decoration: underline; text-decoration-color: transparent; transition: text-decoration-color 0.15s;
}
.slm-text-link:hover { text-decoration-color: var(--color-dark-blue); }
.slm-text-link:disabled { opacity: 0.4; cursor: default; }

.slm-success { text-align: center; padding: 24px 0 8px; }
.slm-success-icon { color: var(--gradient-primary-end); margin: 0 auto 16px; display: block; }
.slm-success-text { font-size: 18px; font-weight: var(--font-weight-bold); color: var(--color-dark-blue); margin: 0; }

.modal-enter-active, .modal-leave-active { transition: opacity 0.22s; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .slm-sheet, .modal-leave-active .slm-sheet { transition: transform 0.22s ease-out; }
.modal-enter-from .slm-sheet, .modal-leave-to .slm-sheet { transform: translateY(32px); }
</style>
